import type { ReactElement } from "react";
import type { ApiTitle } from "@lumi/data";
import { ChevronDown, ChevronUp } from "lucide-react";
import { DEVICE, LAYOUT, RESPONSIVE_LAYOUT, VIEWPORT } from "../../../shared/constants/ui";
import { DetailsTab } from "./DetailsTab";
import { SuggestionsTab } from "./SuggestionsTab";
import { Tabs, type TabType } from "./Tabs";
import { TitleHero, type TitleHeroProps } from "./TitleHero";

export interface TitleContentProps {
  readonly title: ApiTitle;
  readonly activeTab: TabType;
  readonly onTabChange: (tab: TabType) => void;
  readonly isTabsExpanded: boolean;
  readonly onToggleTabsExpanded: () => void;
  readonly isMobile: boolean;
  readonly isDeviceLandscape: boolean;
  readonly onPlay: () => void;
  readonly onPlayTrailer?: () => void;
  readonly hasTrailer: boolean;
  readonly likeState: TitleHeroProps["likeState"];
  readonly watchlistState: TitleHeroProps["watchlistState"];
  readonly transitionTo: (path: string) => void;
}

/**
 * Calculate transform values for the elevator sliding up/down
 *
 * Collapsed (down): Elevator rests with tabs below viewport (only Hero visible)
 * Expanded (up): Elevator slides up to reveal tabs and content
 *
 * Phone Landscape: No transform (stays at relative position, allows scrolling)
 */
function calculateContentTransform(isTabsExpanded: boolean, isDeviceLandscape: boolean): string {
  // In phone landscape, disable transform to allow scrolling
  if (isDeviceLandscape) {
    return "translateY(0)";
  }

  const { width, height } = VIEWPORT.getDimensions();

  // Calculate how far to slide based on device category
  const headerHeight = RESPONSIVE_LAYOUT.getHeaderHeight(width);
  const tabHeight = RESPONSIVE_LAYOUT.getTabCollapsedHeight(width);
  const contentHeight = RESPONSIVE_LAYOUT.getTabContentHeight(width);

  // Phones and portrait tablets: Slide up to reveal content
  if (DEVICE.isPhonePortrait(width, height) || DEVICE.isTabletPortrait(width, height)) {
    if (!isTabsExpanded) {
      // Push elevator down to hide only the content section
      const contentSectionHeight =
        typeof contentHeight === "string" ? height - headerHeight - tabHeight : contentHeight;
      return `translateY(${contentSectionHeight}px)`;
    }
    // When expanded, slide up to reveal content section below tabs
    const slideAmount = RESPONSIVE_LAYOUT.getTabSlideDistance(width);
    return `translateY(-${slideAmount}px)`;
  }

  // Desktop and landscape tablets: Different behavior
  if (!isTabsExpanded) {
    // Position elevator so tabs are below viewport bottom
    const hiddenAmount = tabHeight + (typeof contentHeight === "number" ? contentHeight : 500);
    return `translateY(${hiddenAmount}px)`;
  }

  // When expanded, slide up to reveal tabs and content
  return `translateY(-${LAYOUT.TAB_SLIDE_DISTANCE.DESKTOP}px)`;
}

/**
 * Calculate opacity for TitleHero based on device category and tab state
 * Uses canonical DEVICE system to handle all orientation/size combinations
 */
function calculateHeroOpacity(isTabsExpanded: boolean, isDeviceLandscape: boolean): number {
  // In phone landscape, always keep Hero visible (user can scroll past it)
  if (isDeviceLandscape) {
    return 1;
  }

  const { width, height } = VIEWPORT.getDimensions();

  // Only fade when tabs are expanded
  if (!isTabsExpanded) {
    return 1;
  }

  // Portrait tablets: Keep hero visible (plenty of vertical space)
  if (DEVICE.isTabletPortrait(width, height)) {
    return 1;
  }

  // Desktop with sufficient height: Keep hero visible
  if (DEVICE.isDesktop(width, height)) {
    // Only fade if viewport is too short
    if (height < LAYOUT.MIN_VIEWPORT_HEIGHT_FOR_HERO) {
      return 0;
    }
    return 1;
  }

  // All other cases (phones, landscape tablets): Fade out to prioritize content
  return 0;
}

/**
 * Collapse/Expand Button Component
 */
function CollapseButton({
  isExpanded,
  onClick,
}: {
  readonly isExpanded: boolean;
  readonly onClick: () => void;
}): ReactElement {
  return (
    <button
      onClick={onClick}
      className="text-neutral-400 hover:text-white transition-colors cursor-pointer mb-4"
      aria-label={isExpanded ? "Collapse details" : "Expand details"}
      type="button"
    >
      {isExpanded ? (
        <ChevronDown size={20} className="sm:w-6 sm:h-6" />
      ) : (
        <ChevronUp size={20} className="sm:w-6 sm:h-6" />
      )}
    </button>
  );
}

/**
 * TitleContent Component - "The Elevator" (with Phone Landscape Exception)
 *
 * Elevator Analogy:
 * - Browser viewport = elevator door (what you can see through)
 * - TitleContent = the elevator itself (fixed size, moves up/down as one unit)
 * - TitleHero = top half of elevator (always part of the elevator)
 * - Tabs + Content = bottom half of elevator (always part of the elevator)
 * - Chevron button = elevator button (on the elevator itself)
 *
 * Normal Behavior (Portrait, Tablet, Desktop):
 * - When collapsed (down): Only Hero visible through viewport door
 * - When expanded (up): Both Hero and Tabs visible through viewport door
 * - The entire elevator moves together. Nothing resizes.
 *
 * Phone Landscape Exception (aspect ratio > 1.5, width < 1024px):
 * - Elevator disabled → uses relative positioning
 * - Vertical scrolling enabled on parent container
 * - Tabs auto-expanded and chevron hidden
 * - Hero stays visible (user scrolls past it naturally)
 * - Layout stacks vertically: Hero → Tabs → Content
 */
export function TitleContent({
  title,
  activeTab,
  onTabChange,
  isTabsExpanded,
  onToggleTabsExpanded,
  isMobile: _isMobile,
  isDeviceLandscape,
  onPlay,
  onPlayTrailer,
  hasTrailer,
  likeState,
  watchlistState,
  transitionTo,
}: TitleContentProps): ReactElement {
  const contentTransform = calculateContentTransform(isTabsExpanded, isDeviceLandscape);
  const heroOpacity = calculateHeroOpacity(isTabsExpanded, isDeviceLandscape);

  return (
    <div
      className={isDeviceLandscape ? "relative" : "absolute bottom-0 left-0 right-0"}
      style={{
        transform: isDeviceLandscape ? undefined : contentTransform,
        transition: isDeviceLandscape ? undefined : "transform 300ms ease-in-out",
      }}
    >
      {/* Hero Section - Top half of the elevator */}
      <div className="transition-opacity duration-300 ease-in-out" style={{ opacity: heroOpacity }}>
        <TitleHero
          title={title}
          onPlay={onPlay}
          onPlayTrailer={onPlayTrailer}
          hasTrailer={hasTrailer}
          likeState={likeState}
          watchlistState={watchlistState}
        />
      </div>
      {/* Tabs Navigation with Chevron - Bottom half of the elevator starts here */}
      <div className="relative z-20 px-4 md:px-8 lg:px-16 pb-4 pointer-events-auto flex items-center gap-4">
        {/* Hide chevron in phone landscape (tabs always expanded) */}
        {!isDeviceLandscape && <CollapseButton isExpanded={isTabsExpanded} onClick={onToggleTabsExpanded} />}
        <Tabs activeTab={activeTab} onTabChange={onTabChange} />
      </div>

      {/* Tabs Content Section - Part of the bottom half of the elevator */}
      <section
        className="relative pointer-events-auto z-20"
        style={{
          height: isDeviceLandscape ? "auto" : RESPONSIVE_LAYOUT.getTabContentHeight(VIEWPORT.getWidth()),
        }}
      >
        <div
          className={
            isDeviceLandscape
              ? "pb-8 sm:pb-12 pt-4 sm:pt-6"
              : "h-full overflow-y-auto pb-8 sm:pb-12 pt-4 sm:pt-6 scrollbar-hide"
          }
        >
          {activeTab === "details" && <DetailsTab title={title} />}
          {activeTab === "suggested" && <SuggestionsTab titleId={title.id} transitionTo={transitionTo} />}
          {activeTab === "extras" && (
            <div className="px-4 sm:px-16 text-neutral-400 italic text-sm">Extras coming soon...</div>
          )}
        </div>
      </section>
      {/* Visual overlay — non-interactive so it doesn't block scrolling or pointer events */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/0 pointer-events-none" />
    </div>
  );
}
